import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import * as Icons from "lucide-react";
import { Search, ChevronRight } from "lucide-react";
import { useReadThemeCtx } from "../../lib/ReadThemeContext";
import { ReadThemeToggle } from "../ReadThemeToggle";

export function DocsSidebar({ onOpenSearch }) {
  const [tree, setTree] = useState({ sections: [] });
  const location = useLocation();
  const { theme, toggle } = useReadThemeCtx();
  const isLight = theme === "light";

  useEffect(() => {
    api.get("/docs").then((r) => setTree(r.data)).catch(() => setTree({ sections: [] }));
  }, []);

  // Tone-specific tokens
  const searchBtn = isLight
    ? "border-aubergine/15 bg-bone-soft hover:border-champagne/60 text-aubergine"
    : "border-champ-light/15 bg-aubergine-tier hover:border-champagne/40 text-bone";
  const searchPlaceholder = isLight ? "text-aubergine/55 group-hover:text-aubergine" : "text-bone/60 group-hover:text-bone";
  const kbd = isLight
    ? "border-aubergine/15 text-aubergine/50"
    : "border-champ-light/15 text-bone/40";
  const sectionLabel = isLight ? "text-aubergine/55" : "text-bone/55";
  const linkBase = isLight
    ? "text-aubergine/70 hover:text-aubergine hover:bg-bone-soft"
    : "text-bone/65 hover:text-bone hover:bg-aubergine-tier/60";
  const linkActive = isLight
    ? "bg-bone-soft text-aubergine font-medium"
    : "bg-aubergine-raised text-bone font-medium";

  return (
    <aside className="space-y-6" data-testid="docs-sidebar">
      <div className="flex items-center gap-2">
        <button
          onClick={onOpenSearch}
          data-testid="docs-sidebar-search"
          className={`flex-1 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors group ${searchBtn}`}
        >
          <span className={`flex items-center gap-2 text-sm ${searchPlaceholder}`}>
            <Search className="h-3.5 w-3.5" />
            Search docs…
          </span>
          <kbd className={`font-mono text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 ${kbd}`}>⌘K</kbd>
        </button>
        <ReadThemeToggle theme={theme} onToggle={toggle} data-testid="docs-theme-toggle" />
      </div>

      {tree.sections.map((s) => {
        const Icon = Icons[s.icon] || Icons.Folder;
        return (
          <div key={s.slug} data-testid={`docs-sidebar-section-${s.slug}`}>
            <div className="flex items-center gap-2 mb-2 px-1">
              <Icon className="h-3.5 w-3.5 text-champagne" />
              <p className={`font-mono text-[10px] uppercase tracking-[0.15em] ${sectionLabel}`}>{s.title}</p>
            </div>
            <ul className="space-y-0.5">
              {s.pages.map((p) => {
                const href = `/docs/${s.slug}/${p.slug}`;
                const active = location.pathname === href;
                return (
                  <li key={p.page_id}>
                    <Link
                      to={href}
                      data-testid={`docs-sidebar-link-${s.slug}-${p.slug}`}
                      className={`group flex items-center justify-between gap-2 rounded px-2.5 py-1.5 text-sm transition-colors ${active ? linkActive : linkBase}`}
                    >
                      <span className="truncate">{p.title}</span>
                      {active && <ChevronRight className="h-3 w-3 text-champagne flex-shrink-0" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </aside>
  );
}
